import React, { useState, useEffect } from 'react';
import axiosInstance from "@/api/axios";
import { ResponsiveContainer, RadialBarChart, RadialBar } from 'recharts';
import { useTranslation } from "react-i18next";
import { InfoTooltip } from './Infotooltip';

function getColorScheme(score) {
  if (score >= 70) return { primary: '#10b981', text: 'text-green-600 dark:text-green-400' };
  if (score >= 50) return { primary: '#3b82f6', text: 'text-blue-500 dark:text-blue-400' };
  return               { primary: '#ef4444',  text: 'text-red-500 dark:text-red-400' };
}

export default function RecyclabilityScore({ year, bins, period, customDateRange }) {
  const [animatedScore, setAnimatedScore] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [scoreData, setScoreData] = useState({
    recyclability_score: 0,
    change_rate: 0,
    details: { total_recyclable: 0, total_non_recyclable: 0, total_waste: 0 }
  });
  const { t, i18n } = useTranslation();

  const customDateRangeKey = (() => {
    const r = customDateRange?.[0];
    if (!r?.startDate || !r?.endDate) return null;
    const start = r.startDate instanceof Date ? r.startDate.toISOString() : r.startDate;
    const end   = r.endDate   instanceof Date ? r.endDate.toISOString()   : r.endDate;
    return `${start}__${end}`;
  })();

  const isCustom = Boolean(customDateRangeKey);

  useEffect(() => {
    let isMounted = true;
    const fetchRecyclabilityScore = async () => {
      setLoading(true);
      try {
        const params = { 'bins[]': bins };
        if (isCustom) {
          const r = customDateRange[0];
          params.start_date = r.startDate instanceof Date ? r.startDate.toISOString() : r.startDate;
          params.end_date   = r.endDate   instanceof Date ? r.endDate.toISOString()   : r.endDate;
        } else {
          params.year   = year;
          params.period = period;
        }
        const response = await axiosInstance.get('recyclability_score', { params });
        if (isMounted) { setScoreData(response.data); setLoading(false); }
      } catch (err) {
        console.error('Error fetching recyclability score:', err);
        if (isMounted) { setError(t('recyclabilityScore.error')); setLoading(false); }
      }
    };
    fetchRecyclabilityScore();
    return () => { isMounted = false; };
  }, [bins, year, period, customDateRangeKey]);

  const score = scoreData.recyclability_score;

  useEffect(() => {
    if (animatedScore === score) return;
    const timeout = setTimeout(() => {
      setAnimatedScore(prev => prev < score ? Math.min(prev + 1, score) : Math.max(prev - 1, score));
    }, 20);
    return () => clearTimeout(timeout);
  }, [animatedScore, score]);

  const colorScheme = getColorScheme(score);

  const chartData = [
    { name: 'Max',   value: 100,          fill: '#f3f4f6' },
    { name: 'Score', value: animatedScore, fill: colorScheme.primary }
  ];

  const getPerformanceText = (s) => {
    if (s >= 70) return t('recyclabilityScore.performance.excellent');
    if (s >= 50) return t('recyclabilityScore.performance.meeting');
    return t('recyclabilityScore.performance.needsImprovement');
  };

  // FIX: previously also returned a label for preset period==='year'/'month'
  // selections, not just an actual calendar range — the range badge should
  // only ever appear when the user picked dates from the calendar, same as
  // the other Circularity cards.
  const getRangeLabel = () => {
    if (!isCustom || !customDateRange?.[0]?.startDate || !customDateRange?.[0]?.endDate) return null;
    const r     = customDateRange[0];
    const start = r.startDate instanceof Date ? r.startDate : new Date(r.startDate);
    const end   = r.endDate   instanceof Date ? r.endDate   : new Date(r.endDate);
    const opts  = { day: 'numeric', month: 'short' };
    return `${start.toLocaleDateString(i18n.language, opts)} – ${end.toLocaleDateString(i18n.language, { ...opts, year: 'numeric' })}`;
  };

  if (loading) return (
    // Skeleton — same shape as the loaded card: header, radial gauge ring,
    // centered score, and the performance line underneath.
    <div className="rounded-lg border bg-card text-card-foreground dark:hover:bg-slate-900 flex flex-col w-full h-[200px] motion-safe:animate-pulse" aria-busy="true">
      <span className="sr-only" role="status">{t('recyclabilityScore.loading')}</span>
      <div className="px-4 py-3 flex items-center justify-between">
        <div className="h-4 w-28 rounded bg-gray-200 dark:bg-slate-700" />
        <div className="h-5 w-5 rounded-full bg-gray-200 dark:bg-slate-700" />
      </div>
      <div className="relative flex-1 flex items-center justify-center">
        <div className="relative w-[70%] aspect-[2/1]">
          <svg viewBox="0 0 100 50" className="w-full h-full" aria-hidden="true">
            <path d="M5,50 A45,45 0 0 1 95,50" fill="none" stroke="currentColor" strokeWidth="10" strokeLinecap="round" className="text-gray-200 dark:text-slate-700" />
          </svg>
        </div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-6 w-16 rounded bg-gray-200 dark:bg-slate-700" />
      </div>
      <div className="px-4 pb-3 flex justify-center">
        <div className="h-3 w-24 rounded bg-gray-200 dark:bg-slate-700" />
      </div>
    </div>
  );

  if (error) return (
    <div className="rounded-lg border bg-card text-card-foreground dark:hover:bg-slate-900 flex items-center justify-center w-full h-[200px]">
      <p className="text-sm text-red-500">{error}</p>
    </div>
  );

  return (
    <div className="rounded-lg border bg-card text-card-foreground dark:hover:bg-slate-900 hover:cursor-pointer flex flex-col w-full h-[200px]">
      <div className="px-4 py-3 flex items-center justify-between">
        <h2 className="tracking-tight text-sm font-medium">{t('recyclabilityScore.title')}</h2>
        <div className="flex items-center gap-2">
          {getRangeLabel() && (
            <span className="text-xs text-muted-foreground">{getRangeLabel()}</span>
          )}
          <InfoTooltip text="Score based on how much of your waste was correctly sorted versus sent to residual, weighted by weight." />
        </div>
      </div>

      <div className="relative flex-1">
        <ResponsiveContainer width="100%" height="100%">
          <RadialBarChart
            innerRadius="70%" outerRadius="90%"
            data={chartData} startAngle={180} endAngle={0} barSize={10}
          >
            <RadialBar minAngle={15} background clockWise dataKey="value" cornerRadius={10} />
          </RadialBarChart>
        </ResponsiveContainer>
        <div className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-2xl font-bold ${colorScheme.text}`}>
          {animatedScore.toFixed(1)}%
        </div>
      </div>

      <div className="px-4 pb-3">
        <div className={`text-xs font-medium ${colorScheme.text} text-center`}>
          {getPerformanceText(score)}
        </div>
      </div>
    </div>
  );
}